import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Square, RotateCcw, AlertTriangle, Timer, BookOpenText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import TopHeader from "@/components/TopHeader";
import MicRecorder from "@/components/MicRecorder";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    formatDuration, formatHoursShort, formatMinutesAsHours, formatDate, toBerlin,
} from "@/lib/format";

const ACTIVITIES = [
    "Anfahrt", "Baustelleneinrichtung", "Montage", "Elektroarbeiten",
    "Verkabelung", "Inbetriebnahme", "Fehlersuche", "Nacharbeit",
    "Dokumentation", "Sonstiges",
];

export default function Heute() {
    const [active, setActive] = useState(null);
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState("");
    const [activity, setActivity] = useState("Montage");
    const [description, setDescription] = useState("");
    const [todayTotals, setTodayTotals] = useState(0);
    const [projectTotals, setProjectTotals] = useState({}); // project_id -> total_minutes
    const [diary, setDiary] = useState([]);
    const [tick, setTick] = useState(0);

    const load = useCallback(async () => {
        try {
            const [a, ps, tot, d] = await Promise.all([
                api.get("/time-entries/active"),
                api.get("/projects"),
                api.get("/projects/totals"),
                api.get("/diary"),
            ]);
            setActive(a.data || null);
            setProjects(ps.data || []);
            if (!projectId && (ps.data || [])[0]) setProjectId(ps.data[0].id);
            setProjectTotals(tot.data || {});
            setDiary(d.data || []);
            // today's own minutes
            const today = new Date().toISOString().slice(0, 10);
            const te = await api.get("/time-entries", { params: { date_from: today, date_to: today } });
            const mine = (te.data || []).reduce((s, e) => s + (e.duration_minutes || 0), 0);
            setTodayTotals(mine);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const currentSeconds = useMemo(() => {
        if (!active) return 0;
        const start = new Date(active.start_time).getTime();
        const now = Date.now();
        let pauseMs = (active.pause_minutes || 0) * 60_000;
        if (active.status === "paused" && active.last_pause_start) {
            pauseMs += now - new Date(active.last_pause_start).getTime();
        }
        return Math.max(0, Math.floor((now - start - pauseMs) / 1000));
    }, [active, tick]);

    const start = async () => {
        if (!projectId) return toast.error("Bitte Projekt wählen");
        try {
            const { data } = await api.post("/time-entries/start", {
                project_id: projectId, activity, description,
            });
            setActive(data);
            setDescription("");
            toast.success("Timer gestartet");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const doPause = async () => {
        try { const { data } = await api.post(`/time-entries/${active.id}/pause`); setActive(data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const resume = async () => {
        try { const { data } = await api.post(`/time-entries/${active.id}/resume`); setActive(data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const stop = async () => {
        try {
            await api.post(`/time-entries/${active.id}/stop`);
            setActive(null); toast.success("Zeit gespeichert"); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const activeProject = projects.find((p) => p.id === active?.project_id);
    const focusedProject = activeProject || projects.find((p) => p.id === projectId);
    const focusedTotal = focusedProject ? (projectTotals[focusedProject.id]?.total_minutes || 0) : 0;
    const activeMinutes = active ? Math.floor(currentSeconds / 60) : 0;
    const focusedTotalWithLive = focusedProject && activeProject && activeProject.id === focusedProject.id
        ? focusedTotal + activeMinutes
        : focusedTotal;

    const startDisabled = !projectId || projects.length === 0;

    return (
        <div>
            <TopHeader title="Heute" subtitle={formatDate(new Date())} />
            <main className="mx-auto max-w-2xl px-4 pt-4 safe-bottom" data-testid="page-heute">
                {/* Timer card */}
                <Card className="mes-card overflow-hidden border-mes-border bg-white p-0">
                    <div className="flex items-center justify-between border-b border-mes-border/70 px-4 py-2">
                        <div className="flex items-center gap-2">
                            <span
                                data-testid="timer-state-dot"
                                className={`inline-block h-2 w-2 rounded-full ${
                                    active?.status === "running"
                                        ? "bg-mes-success animate-pulse-dot"
                                        : active?.status === "paused"
                                            ? "bg-mes-warn"
                                            : "bg-mes-border"
                                }`}
                            />
                            <span className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">
                                {active ? (active.status === "running" ? "Aktiv" : "Pausiert") : "Bereit"}
                            </span>
                        </div>
                        <div className="text-[11px] tabular text-mes-muted">
                            {active ? `${toBerlin(active.start_time).format("HH:mm")} Uhr` : "--:--"}
                        </div>
                    </div>
                    <div className="px-4 pt-5">
                        <div className="text-center mes-timer text-[44px] text-mes-ink" data-testid="timer-display">
                            {formatDuration(currentSeconds)}
                        </div>
                        <div className="mt-1 text-center text-sm text-mes-muted">
                            {active
                                ? `${activeProject?.name || "Projekt"} · ${active.activity}`
                                : "Timer bereit"}
                        </div>
                    </div>
                    {!active ? (
                        <div className="space-y-3 px-4 pb-4 pt-4">
                            <div className="grid grid-cols-2 gap-2">
                                <Select value={projectId} onValueChange={setProjectId}>
                                    <SelectTrigger data-testid="select-project" className="h-11 border-mes-border bg-mes-surfaceAlt">
                                        <SelectValue placeholder="Projekt wählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {projects.map((p) => (
                                            <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.id}`}>{p.name}</SelectItem>
                                        ))}
                                        {projects.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-mes-muted">Keine Projekte zugewiesen</div>
                                        )}
                                    </SelectContent>
                                </Select>
                                <Select value={activity} onValueChange={setActivity}>
                                    <SelectTrigger data-testid="select-activity" className="h-11 border-mes-border bg-mes-surfaceAlt">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ACTIVITIES.map((a) => (
                                            <SelectItem key={a} value={a} data-testid={`activity-option-${a}`}>{a}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-start gap-2">
                                <Input
                                    data-testid="input-description"
                                    placeholder="Kurzbeschreibung (optional)"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="h-11 border-mes-border bg-mes-surfaceAlt"
                                />
                                <MicRecorder
                                    testid="mic-timer"
                                    onTranscript={(t) => setDescription((v) => (v ? v + " " + t : t))}
                                />
                            </div>
                            <Button
                                data-testid="btn-start"
                                onClick={start}
                                disabled={startDisabled}
                                className="mes-btn-primary h-14 w-full text-[15px] font-semibold"
                            >
                                <Play className="mr-2 h-5 w-5" strokeWidth={2.4} /> Start
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-2 px-4 pb-4 pt-4">
                            {active.status === "running" ? (
                                <Button data-testid="btn-pause" onClick={doPause}
                                    className="col-span-1 h-14 rounded-lg border border-mes-border bg-white text-[14px] font-semibold text-mes-ink hover:bg-mes-surfaceAlt">
                                    <Pause className="mr-1 h-5 w-5" /> Pause
                                </Button>
                            ) : (
                                <Button data-testid="btn-resume" onClick={resume}
                                    className="col-span-1 h-14 rounded-lg border border-mes-brand/40 bg-mes-brandSoft text-[14px] font-semibold text-mes-brand hover:bg-white">
                                    <RotateCcw className="mr-1 h-5 w-5" /> Weiter
                                </Button>
                            )}
                            <Button data-testid="btn-stop" onClick={stop}
                                className="mes-btn-primary col-span-2 h-14 text-[15px] font-semibold">
                                <Square className="mr-1 h-5 w-5" /> Stop
                            </Button>
                        </div>
                    )}
                </Card>

                {/* Stats row */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <StatCard
                        label="Heute erfasst"
                        value={formatHoursShort(todayTotals + activeMinutes)}
                        testid="stat-today-hours"
                        icon={<Timer className="h-4 w-4 text-mes-brand" strokeWidth={2} />}
                    />
                    <StatCard
                        label="Aktuelles Projekt"
                        value={activeProject?.name?.replace(/^DEMO -\s*/, "") || focusedProject?.name?.replace(/^DEMO -\s*/, "") || "-"}
                        testid="stat-current-project"
                        small
                    />
                </div>

                {focusedProject && (
                    <AllowanceCard project={focusedProject} projectMinutes={focusedTotalWithLive} />
                )}

                <section className="mt-6">
                    <div className="mb-2 flex items-baseline justify-between">
                        <h2 className="font-display text-[15px] font-semibold text-mes-ink">Letzte Bautageseinträge</h2>
                        <Link to="/bautagebuch" className="text-xs font-medium text-mes-brand hover:underline" data-testid="link-more-diary">
                            Alle anzeigen
                        </Link>
                    </div>
                    <div className="space-y-2">
                        {diary.slice(0, 3).map((d) => {
                            const proj = projects.find((p) => p.id === d.project_id);
                            return (
                                <Card key={d.id} className="mes-card p-3" data-testid={`diary-preview-${d.id}`}>
                                    <div className="flex items-center justify-between text-xs text-mes-muted">
                                        <span className="font-medium text-mes-ink">{proj?.name || "Projekt"}</span>
                                        <span className="tabular">{formatDate(d.date + "T00:00:00")}</span>
                                    </div>
                                    <div className="mt-1 line-clamp-2 text-sm text-mes-text">
                                        {d.work_performed || "—"}
                                    </div>
                                </Card>
                            );
                        })}
                        {diary.length === 0 && (
                            <Card className="mes-card border-dashed p-6 text-center text-sm text-mes-muted">
                                <BookOpenText className="mx-auto mb-2 h-5 w-5 text-mes-muted" />
                                Noch keine Einträge.
                            </Card>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}

function StatCard({ label, value, testid, icon, small }) {
    return (
        <Card className="mes-card p-3" data-testid={testid}>
            <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-mes-muted">
                {label}{icon}
            </div>
            <div className={`mt-1 font-display font-bold text-mes-ink ${small ? "text-sm" : "text-2xl tabular"}`}>
                {value}
            </div>
        </Card>
    );
}

function AllowanceCard({ project, projectMinutes }) {
    const allowance = Number(project.stundenkontingent || 0);
    const totalM = Number(projectMinutes || 0);
    const totalH = totalM / 60;
    const pct = allowance > 0 ? Math.min(999, (totalH / allowance) * 100) : 0;
    const level = pct >= 100 ? "kritisch" : pct >= 90 ? "hoch" : pct >= 80 ? "achtung" : "ok";
    const color = level === "kritisch" ? "bg-mes-danger" : level === "hoch" ? "bg-mes-warn"
        : level === "achtung" ? "bg-mes-warn" : "bg-mes-brand";
    return (
        <Card className="mes-card mt-4 p-4" data-testid="allowance-card">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">Kontingent (Projekt gesamt)</div>
                    <div className="mt-0.5 font-display text-[15px] font-semibold text-mes-ink">{project.name}</div>
                </div>
                {level !== "ok" && (
                    <div data-testid="allowance-warning"
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                            level === "kritisch" ? "bg-mes-danger/10 text-mes-danger"
                                : "bg-mes-warnSoft text-mes-warn"
                        }`}>
                        <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.4} />
                        {pct.toFixed(0)}%
                    </div>
                )}
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-mes-surfaceAlt ring-1 ring-mes-border">
                <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-xs text-mes-muted">
                <span>Geleistet: <span className="tabular font-semibold text-mes-ink">{formatHoursShort(totalM)}</span></span>
                <span>Kontingent: <span className="tabular font-semibold text-mes-ink">{formatMinutesAsHours(allowance * 60)} h</span></span>
            </div>
        </Card>
    );
}
